// src/lib/email.ts
/**
 * Email Service using SendGrid
 * 
 * Setup Instructions:
 * 1. Sign up for SendGrid: https://signup.sendgrid.com/
 * 2. Create an API key: Settings > API Keys > Create API Key
 * 3. Add to .env file: VITE_SENDGRID_API_KEY=your_api_key_here
 * 4. Verify sender email in SendGrid (Settings > Sender Authentication)
 */

interface EmailOptions {
  to: string;
  subject: string;
  text?: string;
  html?: string;
  forceSend?: boolean;
}

interface SendGridMessage {
  personalizations: Array<{
    to: Array<{ email: string }>;
    subject: string;
  }>;
  from: {
    email: string;
    name: string;
  };
  content: Array<{
    type: string;
    value: string;
  }>;
}

const SENDGRID_API_KEY = import.meta.env.VITE_SENDGRID_API_KEY;
const FROM_EMAIL = import.meta.env.VITE_FROM_EMAIL || 'noreply@whipt.app';
const FROM_NAME = import.meta.env.VITE_FROM_NAME || 'WHIPT Organizer';

/**
 * Send email via SendGrid API
 */
export async function sendEmail(options: EmailOptions): Promise<boolean> {
  const emailNotificationsEnabled = options.forceSend
    ? true
    : localStorage.getItem('whipt_email_notifications') !== 'false';
  
  if (!emailNotificationsEnabled) {
    console.log('[Email] User has email notifications disabled');
    return false;
  }

  if (!SENDGRID_API_KEY) {
    console.warn('[Email] SendGrid API key not configured. Add VITE_SENDGRID_API_KEY to .env');
    return false;
  }

  try {
    const message: SendGridMessage = {
      personalizations: [
        {
          to: [{ email: options.to }],
          subject: options.subject
        }
      ],
      from: {
        email: FROM_EMAIL,
        name: FROM_NAME
      },
      content: []
    };

    // Add both text and HTML content
    if (options.text) {
      message.content.push({
        type: 'text/plain',
        value: options.text
      });
    }
    
    if (options.html) {
      message.content.push({
        type: 'text/html',
        value: options.html
      });
    }
    
    // Fallback if neither provided
    if (message.content.length === 0) {
      message.content.push({
        type: 'text/plain',
        value: options.text || options.html || ''
      });
    }

    const response = await fetch('https://api.sendgrid.com/v3/mail/send', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${SENDGRID_API_KEY}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(message)
    });

    if (response.ok) {
      console.log('[Email] Sent successfully to:', options.to);
      return true;
    } else {
      const error = await response.text();
      console.error('[Email] SendGrid error:', error);
      return false;
    }
  } catch (error) {
    console.error('[Email] Failed to send:', error);
    return false;
  }
}

/**
 * Send welcome email to new user
 */
export async function sendWelcomeEmail(userEmail: string, userName: string): Promise<boolean> {
  const html = `
    <!DOCTYPE html>
    <html>
    <head>
      <style>
        body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; }
        .container { max-width: 600px; margin: 0 auto; padding: 20px; }
        .header { background: linear-gradient(135deg, #f59e0b 0%, #f97316 100%); color: white; padding: 30px; text-align: center; border-radius: 10px 10px 0 0; }
        .content { background: #f9fafb; padding: 30px; border-radius: 0 0 10px 10px; }
        .button { display: inline-block; background: linear-gradient(135deg, #f59e0b 0%, #f97316 100%); color: white; padding: 12px 30px; text-decoration: none; border-radius: 8px; font-weight: bold; margin: 20px 0; }
        .footer { text-align: center; color: #6b7280; font-size: 12px; margin-top: 30px; }
      </style>
    </head>
    <body>
      <div class="container">
        <div class="header">
          <h1>Welcome to WHIPT! 🎉</h1>
        </div>
        <div class="content">
          <h2>Hi ${userName}!</h2>
          <p>Welcome to WHIPT Organizer - your academic success companion!</p>
          
          <p>You now have access to:</p>
          <ul>
            <li>📚 Class notebooks and organization</li>
            <li>📅 Calendar and assignment tracking</li>
            <li>💬 WHIPT Convo's chat system</li>
            <li>👥 WHIPT Rooms for group collaboration</li>
            <li>🎥 Virtual meeting rooms</li>
            <li>🍕 Late night snack ordering</li>
          </ul>
          
          <p>Get started by creating your first class notebook and adding assignments!</p>
          
          <a href="${window.location.origin}/organizer" class="button">Go to WHIPT Organizer</a>
          
          <p>Need help? Check out our feature tour when you log in, or visit our help center.</p>
          
          <p>Happy organizing!<br>The WHIPT Team</p>
        </div>
        <div class="footer">
          <p>© 2026 WHIPT Organizer by Pronoia Wellness</p>
          <p>Powered by Black Betterment Quest</p>
        </div>
      </div>
    </body>
    </html>
  `;

  return sendEmail({
    to: userEmail,
    subject: 'Welcome to WHIPT Organizer! 🎉',
    html
  });
}

/**
 * Send assignment due date reminder
 */
export async function sendAssignmentReminder(
  userEmail: string,
  assignmentTitle: string,
  dueDate: string,
  className: string
): Promise<boolean> {
  const formattedDate = new Date(dueDate).toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });

  const html = `
    <!DOCTYPE html>
    <html>
    <head>
      <style>
        body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; }
        .container { max-width: 600px; margin: 0 auto; padding: 20px; }
        .header { background: linear-gradient(135deg, #f59e0b 0%, #f97316 100%); color: white; padding: 30px; text-align: center; border-radius: 10px 10px 0 0; }
        .content { background: #f9fafb; padding: 30px; border-radius: 0 0 10px 10px; }
        .alert { background: #fef3c7; border-left: 4px solid #f59e0b; padding: 15px; margin: 20px 0; border-radius: 5px; }
        .button { display: inline-block; background: linear-gradient(135deg, #f59e0b 0%, #f97316 100%); color: white; padding: 12px 30px; text-decoration: none; border-radius: 8px; font-weight: bold; margin: 20px 0; }
        .footer { text-align: center; color: #6b7280; font-size: 12px; margin-top: 30px; }
      </style>
    </head>
    <body>
      <div class="container">
        <div class="header">
          <h1>📅 Assignment Reminder</h1>
        </div>
        <div class="content">
          <div class="alert">
            <strong>⏰ Upcoming Deadline</strong>
          </div>
          
          <h2>${assignmentTitle}</h2>
          <p><strong>Class:</strong> ${className}</p>
          <p><strong>Due:</strong> ${formattedDate}</p>
          
          <p>Don't forget to complete your assignment before the deadline!</p>
          
          <a href="${window.location.origin}/organizer" class="button">View in WHIPT</a>
          
          <p>Good luck!</p>
        </div>
        <div class="footer">
          <p>© 2026 WHIPT Organizer</p>
          <p><a href="${window.location.origin}/organizer/settings">Manage notification preferences</a></p>
        </div>
      </div>
    </body>
    </html>
  `;

  return sendEmail({
    to: userEmail,
    subject: `⏰ Reminder: ${assignmentTitle} due soon`,
    html
  });
}

/**
 * Send new message notification
 */
export async function sendMessageNotification(
  userEmail: string,
  senderName: string,
  messagePreview: string,
  isPrivate: boolean = true
): Promise<boolean> {
  const chatType = isPrivate ? 'private message' : 'room message';
  
  const html = `
    <!DOCTYPE html>
    <html>
    <head>
      <style>
        body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; }
        .container { max-width: 600px; margin: 0 auto; padding: 20px; }
        .header { background: linear-gradient(135deg, #8b5cf6 0%, #ec4899 100%); color: white; padding: 30px; text-align: center; border-radius: 10px 10px 0 0; }
        .content { background: #f9fafb; padding: 30px; border-radius: 0 0 10px 10px; }
        .message { background: white; border-left: 4px solid #8b5cf6; padding: 15px; margin: 20px 0; border-radius: 5px; }
        .button { display: inline-block; background: linear-gradient(135deg, #8b5cf6 0%, #ec4899 100%); color: white; padding: 12px 30px; text-decoration: none; border-radius: 8px; font-weight: bold; margin: 20px 0; }
        .footer { text-align: center; color: #6b7280; font-size: 12px; margin-top: 30px; }
      </style>
    </head>
    <body>
      <div class="container">
        <div class="header">
          <h1>💬 New Message</h1>
        </div>
        <div class="content">
          <p><strong>${senderName}</strong> sent you a ${chatType}:</p>
          
          <div class="message">
            ${messagePreview.substring(0, 150)}${messagePreview.length > 150 ? '...' : ''}
          </div>
          
          <a href="${window.location.origin}/organizer" class="button">View Message</a>
        </div>
        <div class="footer">
          <p>© 2026 WHIPT Organizer</p>
          <p><a href="${window.location.origin}/organizer/settings">Manage notification preferences</a></p>
        </div>
      </div>
    </body>
    </html>
  `;

  return sendEmail({
    to: userEmail,
    subject: `💬 New message from ${senderName}`,
    html
  });
}

/**
 * Send password reset email
 */
export async function sendPasswordResetEmail(
  userEmail: string,
  resetToken: string
): Promise<boolean> {
  const resetLink = `${window.location.origin}/reset-password?token=${resetToken}`;
  
  const html = `
    <!DOCTYPE html>
    <html>
    <head>
      <style>
        body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; }
        .container { max-width: 600px; margin: 0 auto; padding: 20px; }
        .header { background: linear-gradient(135deg, #3b82f6 0%, #06b6d4 100%); color: white; padding: 30px; text-align: center; border-radius: 10px 10px 0 0; }
        .content { background: #f9fafb; padding: 30px; border-radius: 0 0 10px 10px; }
        .alert { background: #dbeafe; border-left: 4px solid #3b82f6; padding: 15px; margin: 20px 0; border-radius: 5px; }
        .button { display: inline-block; background: linear-gradient(135deg, #3b82f6 0%, #06b6d4 100%); color: white; padding: 12px 30px; text-decoration: none; border-radius: 8px; font-weight: bold; margin: 20px 0; }
        .footer { text-align: center; color: #6b7280; font-size: 12px; margin-top: 30px; }
      </style>
    </head>
    <body>
      <div class="container">
        <div class="header">
          <h1>🔐 Password Reset</h1>
        </div>
        <div class="content">
          <p>You requested to reset your password for your WHIPT account.</p>
          
          <div class="alert">
            <strong>⚠️ This link expires in 1 hour</strong>
          </div>
          
          <p>Click the button below to reset your password:</p>
          
          <a href="${resetLink}" class="button">Reset Password</a>
          
          <p>If you didn't request this, you can safely ignore this email.</p>
          
          <p style="color: #6b7280; font-size: 12px; margin-top: 30px;">
            Or copy and paste this link:<br>
            ${resetLink}
          </p>
        </div>
        <div class="footer">
          <p>© 2026 WHIPT Organizer</p>
        </div>
      </div>
    </body>
    </html>
  `;

  return sendEmail({
    to: userEmail,
    subject: '🔐 Reset Your WHIPT Password',
    html,
    text: `We received a request to reset your WHIPT password. Use this link within the next hour: ${resetLink}`,
    forceSend: true
  });
}
